import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Production readiness capstone (part 50).
 *
 * The page does not ask the operator to tick boxes. Pressing "ประเมินใหม่" makes the API call the
 * live edge (healthz, an unauthenticated protected route, login, an authenticated dashboard call
 * and /metrics) and query the development database (RLS coverage, audit chain, append-only
 * trigger, migration head, retention, compliance register and audit partitions). Every check on
 * this screen carries the text the server measured; the score is the weighted result and a failed
 * blocking gate caps it regardless of how many cosmetic checks passed.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type Status = 'pass' | 'warn' | 'fail'
type Verdict = 'ready' | 'conditional' | 'not_ready' | 'blocked'

interface CategoryScore {
  category: string
  label_th: string
  weight: number
  score: number
  passed: number
  warned: number
  failed: number
  blocking_failed: number
}

interface Gate {
  check_id: string
  title_th: string
  status: Status
  status_label_th: string
  evidence_th: string
}

interface Run {
  id: string
  target_url: string
  score: number
  raw_score: number
  grade: string
  verdict: Verdict
  summary_th: string
  checks_total: number
  checks_passed: number
  checks_warned: number
  checks_failed: number
  blocking_failed: number
  categories: CategoryScore[]
  gates: Gate[]
  score_cap: number
  capped: boolean
  started_at: string
  finished_at: string
  duration_ms: number
  triggered_by?: string
  triggered_by_role: string
  created_at: string
}

interface CheckRow {
  id: string
  run_id: string
  check_id: string
  category: string
  title_th: string
  status: Status
  weight: number
  blocking: boolean
  evidence_th: string
  remediation_th: string
  duration_ms: number
  position: number
}

interface Detail {
  run: Run
  checks: CheckRow[]
}

interface PolicyCategory {
  category: string
  label_th: string
  weight: number
}

interface Policy {
  categories: PolicyCategory[]
  checks: { id: string; category: string; title_th: string; weight: number; blocking: boolean }[]
  ready_score: number
  conditional_score: number
  blocking_cap: number
}

const STATUS_LABEL: Record<Status, string> = {
  pass: 'ผ่าน',
  warn: 'ควรปรับปรุง',
  fail: 'ไม่ผ่าน',
}

const STATUS_TONE: Record<Status, string> = {
  pass: 'var(--color-emerald-soft)',
  warn: 'var(--color-gold)',
  fail: 'var(--color-crimson)',
}

const VERDICT_LABEL: Record<Verdict, string> = {
  ready: 'พร้อมใช้งาน',
  conditional: 'พร้อมแบบมีเงื่อนไข',
  not_ready: 'ยังไม่พร้อม',
  blocked: 'ถูกบล็อก',
}

const VERDICT_TONE: Record<Verdict, string> = {
  ready: 'var(--color-emerald-soft)',
  conditional: 'var(--color-gold)',
  not_ready: 'var(--color-amber)',
  blocked: 'var(--color-crimson)',
}

const CATEGORY_LABEL: Record<string, string> = {
  security: 'ความปลอดภัย',
  data_integrity: 'ความถูกต้องของข้อมูล',
  observability: 'การสังเกตการณ์',
  deploy: 'การติดตั้งใช้งาน',
  compliance: 'การปฏิบัติตามข้อกำหนด',
}

const CATEGORY_ORDER = ['security', 'data_integrity', 'observability', 'deploy', 'compliance']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function scoreTone(score: number): string {
  if (score >= 90) return 'var(--color-emerald)'
  if (score >= 70) return 'var(--color-gold)'
  return 'var(--color-crimson)'
}

function hostOf(target: string): string {
  try {
    return new URL(target).host
  } catch {
    return target
  }
}

function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} วินาที`
}

export function Readiness() {
  const { me } = useAuth()
  const canRun = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const [selectedId, setSelectedId] = useState('')
  const [category, setCategory] = useState('all')
  const [status, setStatus] = useState('all')
  const [notice, setNotice] = useState('')

  const policy = useQuery({
    queryKey: ['readiness-policy'],
    queryFn: () => api<Policy>('/v1/readiness/policy'),
    staleTime: Infinity,
  })

  const runs = useQuery({
    queryKey: ['readiness-runs'],
    queryFn: () => api<Run[]>('/v1/readiness/runs?limit=30'),
  })

  const activeId = selectedId || runs.data?.[0]?.id || ''
  const detail = useQuery({
    queryKey: ['readiness-run', activeId],
    queryFn: () => api<Detail>(`/v1/readiness/runs/${activeId}`),
    enabled: activeId !== '',
    retry: false,
  })

  const runAssessment = useMutation({
    mutationFn: () => apiWrite<Detail>('/v1/readiness/runs', 'POST', {}),
    onSuccess: (saved) => {
      setSelectedId(saved.run.id)
      setNotice(`ประเมินเสร็จใน ${duration(saved.run.duration_ms)} · คะแนน ${saved.run.score}/100 · ${VERDICT_LABEL[saved.run.verdict]}`)
      void qc.invalidateQueries({ queryKey: ['readiness-runs'] })
      qc.setQueryData(['readiness-run', saved.run.id], saved)
    },
    onError: (err) => setNotice(errorText(err, 'ประเมินความพร้อมไม่สำเร็จ')),
  })

  const current = detail.data?.run ?? null
  const checks = detail.data?.checks ?? []
  const filtered = useMemo(() => {
    return checks.filter((row) => {
      if (category !== 'all' && row.category !== category) return false
      if (status !== 'all' && row.status !== status) return false
      return true
    })
  }, [checks, category, status])

  const categories = useMemo(() => {
    const rows = current?.categories ?? []
    return [...rows].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category))
  }, [current])

  const policyCategories = policy.data?.categories ?? []

  return (
    <>
      <PageHead
        title="ความพร้อมก่อนใช้งานจริง"
        sub="ประเมินจากโฮสต์จริงและฐานข้อมูลจริง · คะแนนถ่วงน้ำหนักตามหมวด · เกณฑ์บังคับที่ไม่ผ่านจะบล็อกผลแม้คะแนนรวมจะสูง"
        actions={canRun ? (
          <button className="btn btn-primary" onClick={() => { setNotice(''); runAssessment.mutate() }}
            disabled={runAssessment.isPending}>
            {runAssessment.isPending ? 'กำลังประเมิน…' : 'ประเมินใหม่'}
          </button>
        ) : undefined}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip chip-on">เป้าหมาย {hostOf(current?.target_url ?? 'https://auditai-edge.toojinda2.workers.dev')}</span>
        <span className="chip">เกณฑ์พร้อมใช้งาน {policy.data?.ready_score ?? 90} คะแนน</span>
        <span className="chip">เพดานเมื่อเกณฑ์บังคับไม่ผ่าน {policy.data?.blocking_cap ?? 79} คะแนน</span>
        <span className="chip">การตรวจทั้งหมด {policy.data?.checks.length ?? 18} รายการ</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {runs.isLoading ? (
        <div className="stack"><Skel h={90} /><Skel h={220} /></div>
      ) : current === null ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีการประเมินความพร้อม"
            body="เริ่มประเมินครั้งแรก ระบบจะเรียกโฮสต์จริงและตรวจฐานข้อมูล แล้วบันทึกผลพร้อมหลักฐานทุกข้อ"
          />
          {canRun ? (
            <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => runAssessment.mutate()} disabled={runAssessment.isPending}>
                {runAssessment.isPending ? 'กำลังประเมิน…' : 'เริ่มประเมิน'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid-4" style={{ marginBottom: 16 }}>
            <Stat
              label="คะแนนความพร้อม"
              value={<span className="tnum">{current.score}</span>}
              unit="/100"
              foot={current.capped ? `คะแนนดิบ ${current.raw_score.toFixed(1)} ถูกจำกัดที่ ${current.score_cap}` : `เกรด ${current.grade} · คะแนนดิบ ${current.raw_score.toFixed(1)}`}
            />
            <Stat
              label="ผลตัดสิน"
              value={<span style={{ color: VERDICT_TONE[current.verdict], fontSize: 24 }}>{VERDICT_LABEL[current.verdict]}</span>}
              foot={current.summary_th}
            />
            <Stat
              label="ตรวจผ่าน"
              value={current.checks_passed}
              unit={`/ ${current.checks_total}`}
              foot={`ควรปรับปรุง ${current.checks_warned} · ไม่ผ่าน ${current.checks_failed}`}
            />
            <Stat
              label="เกณฑ์บังคับที่ไม่ผ่าน"
              value={current.blocking_failed}
              foot={`ใช้เวลา ${duration(current.duration_ms)} · ${thaiDateTime(current.finished_at)}`}
            />
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <div className="card-label">คะแนนรวม</div>
              <span className="dim tnum" style={{ fontSize: 12 }}>
                {current.checks_total - current.checks_failed - current.checks_warned} ผ่าน · {current.checks_warned} เตือน · {current.checks_failed} ไม่ผ่าน
              </span>
            </div>
            <div className="bar" style={{ height: 8 }}>
              <i style={{ width: `${current.score}%`, background: scoreTone(current.score) }} />
            </div>
            <div className="row" style={{ gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
              <span className="dim" style={{ fontSize: 12 }}>เป้าหมาย {hostOf(current.target_url)}</span>
              <span className="dim" style={{ fontSize: 12 }}>เริ่ม {thaiDateTime(current.started_at)}</span>
              <span className="dim" style={{ fontSize: 12 }}>สั่งโดย {current.triggered_by_role || 'ระบบ'}</span>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div className="card-label" style={{ marginBottom: 10 }}>คะแนนตามหมวด</div>
            <div className="grid-3">
              {categories.map((row) => (
                <div className="card card-pad" key={row.category}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span className="card-label">{row.label_th || CATEGORY_LABEL[row.category] || row.category}</span>
                    <span className="tnum" style={{ color: scoreTone(row.score), fontSize: 22 }}>{row.score}</span>
                  </div>
                  <div className="bar" style={{ marginTop: 10, height: 5 }}>
                    <i style={{ width: `${row.score}%`, background: scoreTone(row.score) }} />
                  </div>
                  <div className="dim tnum" style={{ fontSize: 11.5, marginTop: 9 }}>
                    น้ำหนัก {row.weight}% · ผ่าน {row.passed} · เตือน {row.warned} · ไม่ผ่าน {row.failed}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {current.gates.some((gate) => gate.status !== 'pass') ? (
            <div className="card card-pad" style={{ marginBottom: 16 }}>
              <div className="card-label" style={{ marginBottom: 10 }}>เกณฑ์บังคับที่ต้องแก้ก่อนเปิดใช้งาน</div>
              <div className="stack">
                {current.gates.filter((gate) => gate.status !== 'pass').map((gate) => (
                  <div key={gate.check_id} style={{ borderLeft: `3px solid ${STATUS_TONE[gate.status]}`, paddingLeft: 12 }}>
                    <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13 }}>{gate.title_th}</span>
                      <span className="chip" style={{ color: STATUS_TONE[gate.status] }}>{STATUS_LABEL[gate.status]}</span>
                    </div>
                    <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{gate.evidence_th}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              <div className="card-label">รายละเอียดการตรวจ</div>
              <div className="row" style={{ gap: 8 }}>
                <select className="input" style={{ width: 190, height: 32 }} value={category}
                  onChange={(event) => setCategory(event.target.value)} aria-label="กรองตามหมวด">
                  <option value="all">ทุกหมวด</option>
                  {CATEGORY_ORDER.map((value) => (
                    <option key={value} value={value}>{CATEGORY_LABEL[value]}</option>
                  ))}
                </select>
                <select className="input" style={{ width: 150, height: 32 }} value={status}
                  onChange={(event) => setStatus(event.target.value)} aria-label="กรองตามสถานะ">
                  <option value="all">ทุกสถานะ</option>
                  <option value="pass">ผ่าน</option>
                  <option value="warn">ควรปรับปรุง</option>
                  <option value="fail">ไม่ผ่าน</option>
                </select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <Empty title="ไม่พบรายการที่ตรงกับตัวกรอง" body="เลือกหมวดหรือสถานะอื่นเพื่อดูผลการตรวจที่เหลือ" />
            ) : (
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>สถานะ</th>
                      <th>การตรวจสอบ</th>
                      <th>หมวด</th>
                      <th className="num">น้ำหนัก</th>
                      <th>หลักฐานที่วัดได้</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr key={row.check_id}>
                        <td>
                          <span className="sev" style={{ color: STATUS_TONE[row.status] }}>
                            <i className="sev-dot" style={{ background: STATUS_TONE[row.status] }} />
                            {STATUS_LABEL[row.status]}
                          </span>
                        </td>
                        <td>
                          <div>{row.title_th}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                            {row.check_id}{row.blocking ? ' · เกณฑ์บังคับ' : ''}
                          </div>
                        </td>
                        <td className="dim" style={{ fontSize: 12 }}>{CATEGORY_LABEL[row.category] ?? row.category}</td>
                        <td className="num tnum">{row.weight}</td>
                        <td style={{ maxWidth: 520, whiteSpace: 'normal', fontSize: 12.5 }}>
                          {row.evidence_th}
                          {row.status !== 'pass' && row.remediation_th ? (
                            <div className="dim" style={{ fontSize: 11.5, marginTop: 5 }}>แนวทางแก้: {row.remediation_th}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label" style={{ marginBottom: 10 }}>ประวัติการประเมิน</div>
            {runs.data && runs.data.length > 0 ? (
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th className="num">คะแนน</th>
                      <th>ผลตัดสิน</th>
                      <th className="num">ผ่าน / เตือน / ไม่ผ่าน</th>
                      <th>เป้าหมาย</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {runs.data.map((row) => (
                      <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(row.id)}>
                        <td className="dim tnum">{thaiDateTime(row.created_at)}</td>
                        <td className="num tnum" style={{ color: scoreTone(row.score) }}>{row.score}</td>
                        <td style={{ color: VERDICT_TONE[row.verdict] }}>{VERDICT_LABEL[row.verdict]}</td>
                        <td className="num tnum">{row.checks_passed} / {row.checks_warned} / {row.checks_failed}</td>
                        <td className="dim" style={{ fontSize: 12 }}>{hostOf(row.target_url)}</td>
                        <td className="num">
                          <button className="btn btn-sm" onClick={(event) => { event.stopPropagation(); setSelectedId(row.id) }}>
                            เปิด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dim" style={{ fontSize: 12.5 }}>ยังไม่มีประวัติ</div>
            )}
          </div>
        </>
      )}

      {policyCategories.length > 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="card-label" style={{ marginBottom: 8 }}>นโยบายการให้คะแนน</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {policyCategories.map((row) => (
              <span className="chip" key={row.category}>{row.label_th} {row.weight}%</span>
            ))}
            <span className="chip gold">ผ่านเมื่อไม่มีข้อใดไม่ผ่านและคะแนน ≥ {policy.data?.ready_score ?? 90}</span>
            <span className="chip">ต่ำกว่า {policy.data?.conditional_score ?? 70} ถือว่าไม่พร้อม</span>
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
            ทุกข้อมีน้ำหนักในหมวดของตน หมวดละ 100 คะแนน และนำมาถ่วงด้วยน้ำหนักหมวด การตรวจที่ไม่ถูกวัดถือว่าไม่ผ่าน
            ไม่ใช่ข้าม · ข้อมูลทั้งหมดถูกบันทึกแบบเพิ่มอย่างเดียว (append-only)
          </div>
        </div>
      ) : null}

      {detail.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <span className="dim" style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>
            {errorText(detail.error, 'อ่านผลการประเมินไม่สำเร็จ')}
          </span>
        </div>
      ) : null}

      {runs.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <span className="dim" style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>
            {errorText(runs.error, 'อ่านประวัติการประเมินไม่สำเร็จ')}
          </span>
        </div>
      ) : null}

      {policy.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <span className="dim" style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>
            {errorText(policy.error, 'อ่านนโยบายการให้คะแนนไม่สำเร็จ')}
          </span>
        </div>
      ) : null}
    </>
  )
}
